import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Users, Eye, UserRound, MapPin, Loader2, BarChart3, Globe } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { adminApi } from '../lib/adminApi';

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };

const PRESETS = [
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: '90', label: 'Last 90 days', days: 90 },
  { key: 'all', label: 'All time', days: 730 },
];
const GRANULARITIES = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const fmtBucket = (b, gran) => {
  if (gran === 'month') {
    const [y, m] = b.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
  }
  const d = new Date(b + 'T00:00:00');
  const s = d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return gran === 'week' ? `Wk ${s}` : s;
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white border border-[#eaeaea] p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
      <Icon className="w-3.5 h-3.5 text-[#eb5381]" /> {label}
    </div>
    <div className="mt-3 font-serif text-4xl">{value.toLocaleString()}</div>
  </div>
);

export default function AdminAnalytics() {
  const [from, setFrom] = useState(iso(daysAgo(30)));
  const [to, setTo] = useState(iso(new Date()));
  const [granularity, setGranularity] = useState('day');
  const [preset, setPreset] = useState('30');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.analytics(from, to, granularity)
      .then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || 'Could not load analytics'))
      .finally(() => setLoading(false));
  }, [from, to, granularity]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    setPreset(p.key);
    setFrom(iso(daysAgo(p.days)));
    setTo(iso(new Date()));
  };

  const chartData = useMemo(
    () => (data?.timeseries || []).map(t => ({ ...t, label: fmtBucket(t.bucket, granularity) })),
    [data, granularity]
  );
  const maxRegion = Math.max(1, ...((data?.regions || []).map(r => r.pageviews)));
  const maxSource = Math.max(1, ...((data?.sources || []).map(s => s.pageviews)));
  const summary = data?.summary || { visitors: 0, pageviews: 0, artist_views: 0 };

  return (
    <div data-testid="admin-analytics-page" className="max-w-6xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">Insights</div>
        <h1 className="mt-1 font-serif text-3xl md:text-4xl">Site Analytics</h1>
        <p className="mt-3 text-sm text-neutral-600 max-w-2xl leading-relaxed">
          Visitors, page views and per-artist views tracked on your live site. Pick a date range and group the trend by day, week or month.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white border border-[#eaeaea] p-4 md:p-5 mb-8" data-testid="analytics-controls">
        <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
          <div className="flex flex-wrap gap-2" data-testid="analytics-presets">
            {PRESETS.map(p => (
              <button
                key={p.key}
                onClick={() => applyPreset(p)}
                data-testid={`preset-${p.key}`}
                className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                  preset === p.key ? 'bg-[#eb5381] text-white border-[#eb5381]' : 'border-[#eaeaea] text-neutral-700 hover:border-[#eb5381]'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">From</span>
              <input type="date" value={from} max={to} onChange={(e) => { setFrom(e.target.value); setPreset(''); }} data-testid="analytics-from" className="border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">To</span>
              <input type="date" value={to} min={from} max={iso(new Date())} onChange={(e) => { setTo(e.target.value); setPreset(''); }} data-testid="analytics-to" className="border border-[#eaeaea] focus:border-[#eb5381] outline-none px-3 py-2 text-sm" />
            </label>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Group by</span>
            <div className="flex" data-testid="analytics-granularity">
              {GRANULARITIES.map(g => (
                <button
                  key={g.key}
                  onClick={() => setGranularity(g.key)}
                  data-testid={`granularity-${g.key}`}
                  className={`px-4 py-2 text-[10px] uppercase tracking-[0.2em] border transition-colors ${
                    granularity === g.key ? 'bg-black text-white border-black' : 'border-[#eaeaea] text-neutral-700 hover:border-black'
                  }`}
                >
                  {g.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-3 text-sm uppercase tracking-[0.25em] text-neutral-500 py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading analytics…
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <StatCard icon={Users} label="Unique Visitors" value={summary.visitors} testid="stat-visitors" />
            <StatCard icon={Eye} label="Page Views" value={summary.pageviews} testid="stat-pageviews" />
            <StatCard icon={UserRound} label="Artist Profile Views" value={summary.artist_views} testid="stat-artist-views" />
          </div>

          {/* Trend chart */}
          <section className="bg-white border border-[#eaeaea] p-5 md:p-6 mb-8" data-testid="analytics-trend">
            <div className="flex items-center gap-2 mb-5">
              <BarChart3 className="w-4 h-4 text-[#eb5381]" />
              <h2 className="font-serif text-2xl">Traffic Trend</h2>
            </div>
            {chartData.length === 0 ? (
              <div className="text-sm text-neutral-500 border border-dashed border-neutral-300 p-10 text-center">No data for this range yet.</div>
            ) : (
              <div style={{ width: '100%', height: 320 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#999" />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} stroke="#999" />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 0, border: '1px solid #eaeaea' }} />
                    <Bar dataKey="pageviews" name="Page views" fill="#eb5381" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="visitors" name="Visitors" fill="#a465a8" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Traffic Sources */}
            <section className="bg-white border border-[#eaeaea] p-5 md:p-6" data-testid="analytics-sources">
              <div className="flex items-center gap-2 mb-2">
                <Globe className="w-4 h-4 text-[#eb5381]" />
                <h2 className="font-serif text-2xl">Traffic Sources</h2>
              </div>
              <p className="text-xs text-neutral-500 mb-5">
                Where visitors came from. Tag your links with <code className="bg-[#f5f5f5] px-1">?utm_source=instagram</code> for precise attribution; otherwise it's inferred from the referrer.
              </p>
              {(data?.sources || []).length === 0 ? (
                <div className="text-sm text-neutral-500">No source data yet.</div>
              ) : (
                <ul className="space-y-3">
                  {data.sources.slice(0, 15).map(s => (
                    <li key={s.label} data-testid={`source-row-${s.label}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="truncate pr-2">{s.label}</span>
                        <span className="text-neutral-500 tabular-nums shrink-0">{s.visitors} visitor{s.visitors === 1 ? '' : 's'} · {s.pageviews} views</span>
                      </div>
                      <div className="h-2 bg-[#f5f5f5]">
                        <div className="h-2 bg-[#a465a8]" style={{ width: `${Math.max(4, (s.pageviews / maxSource) * 100)}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Regions */}
            <section className="bg-white border border-[#eaeaea] p-5 md:p-6" data-testid="analytics-regions">
              <div className="flex items-center gap-2 mb-5">
                <MapPin className="w-4 h-4 text-[#eb5381]" />
                <h2 className="font-serif text-2xl">Visitors by Region</h2>
              </div>
              {(data?.regions || []).length === 0 ? (
                <div className="text-sm text-neutral-500">No location data yet.</div>
              ) : (
                <ul className="space-y-3">
                  {data.regions.slice(0, 15).map(r => (
                    <li key={r.label} data-testid={`region-row-${r.label}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="truncate pr-2">{r.label}</span>
                        <span className="text-neutral-500 tabular-nums shrink-0">{r.visitors} visitor{r.visitors === 1 ? '' : 's'} · {r.pageviews} views</span>
                      </div>
                      <div className="h-2 bg-[#f5f5f5]">
                        <div className="h-2 bg-[#eb5381]" style={{ width: `${Math.max(4, (r.pageviews / maxRegion) * 100)}%` }} />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Artist views */}
          <section className="bg-white border border-[#eaeaea] p-5 md:p-6 mt-8" data-testid="analytics-artists">
            <div className="flex items-center gap-2 mb-5">
              <UserRound className="w-4 h-4 text-[#eb5381]" />
              <h2 className="font-serif text-2xl">Most Viewed Artists</h2>
            </div>
            {(data?.artists || []).length === 0 ? (
              <div className="text-sm text-neutral-500">No artist views yet.</div>
            ) : (
              <ol className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {data.artists.slice(0, 20).map((a, i) => (
                  <li key={a.slug} className="flex items-center gap-3 border border-[#eaeaea] p-2.5" data-testid={`artist-view-row-${a.slug}`}>
                    <div className="w-6 text-center text-[10px] uppercase tracking-[0.2em] text-neutral-400">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-lg leading-tight truncate">{a.name}</div>
                      {a.city && <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">{a.city}</div>}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-serif text-xl">{a.views.toLocaleString()}</div>
                      <div className="text-[9px] uppercase tracking-[0.2em] text-neutral-400">views</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </>
      )}
    </div>
  );
}
